import { AddressController } from '@adornis/address/client/address-controller.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { FormField } from '@adornis/formfield/form-field.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import { type Adresse, ZipCity } from '@chancenwerk/shared/adresse.js';
import '../design/components/cr-dropdown-search-selection.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { filter, switchMap, takeUntil } from 'rxjs';

/**
 * @customElement chancen-component-zip-city-picker
 */
@customElement('chancen-component-zip-city-picker')
export class ChancenComponentZipCityPicker extends FormField<Maybe<ZipCity>> {
  @property({ attribute: 'placeholder-zip', type: String, reflect: true }) public placeholderZip = 'PLZ';
  @property({ attribute: 'placeholder-city', type: String, reflect: true }) public placeholderCity = 'Stadt';
  @property({ attribute: true, type: Boolean, reflect: true }) public clearable = false;
  @property({ attribute: true, type: Boolean, reflect: true }) public allCountries = false;
  @property({ attribute: 'only-cities', type: Boolean, reflect: true }) public onlyCities = false;
  @property({ attribute: 'space', type: String, reflect: true }) public space = 'md';
  @property({ attribute: true, type: Boolean, reflect: true }) public required = false;

  @property({ attribute: false }) enabledFields = new RXController<null | BaseQLSelectionSet<Adresse>>(this, null);

  @property({ attribute: false }) public form = new FormController<ZipCity>(this, this.value.observable, {
    enabledFields: this.enabledFields.observable,
  });

  // @ts-expect-error _whenChanging state to update the dom
  @state() private readonly _changing = new RXController(
    this,
    this.value.observable.pipe(
      filter(Boolean),
      switchMap(value => value.whenChanging),
    ),
  );

  // Made public to incorporate it into another addressController
  public addressController = new AddressController(this, {
    language: 'de',
    countries: this.allCountries ? [] : undefined,
  });

  override connectedCallback(): void {
    super.connectedCallback();
    this.value.observable.pipe(takeUntil(this.disconnected)).subscribe(value => {
      if (!value) this.value.next(new ZipCity({}));
    });
  }

  override render() {
    return html`
      <cr-flex space=${this.space} horizontal wrap>
        ${!this.onlyCities
          ? html`
              <cr-dropdown-search-selection
                ?disabled=${this.disabled}
                ${css({ minWidth: '200px' })}
                flex
                placeholder=${this.placeholderZip.concat(this.required ? ' *' : '')}
                clearable=${this.clearable}
                ${this.addressController.zip()}
                ${this.form.field('zip')}
                @value-picked=${async () => {
                  this._emitValuePicked();
                }}
              ></cr-dropdown-search-selection>
            `
          : nothing}

        <cr-dropdown-search-selection
          ?disabled=${this.disabled}
          ${css({ minWidth: '200px' })}
          flex
          placeholder=${this.placeholderCity.concat(this.required ? ' *' : '')}
          clearable=${this.clearable}
          ${this.addressController.city()}
          ${this.form.field('city')}
          @value-picked=${async () => {
            this._emitValuePicked();
          }}
        ></cr-dropdown-search-selection>
      </cr-flex>
    `;
  }

  private _emitValuePicked() {
    const event = new CustomEvent('value-picked', { detail: { value: this.value.value } });
    this.dispatchEvent(event);
  }
}
